import { z } from "zod";
// Quantity is capped per line and the sum across lines is capped again below
// -- this is an unauthenticated route that creates a Postgres row and a
// Stripe object per call, so both caps exist to bound abuse, not to model a
// real cart limit.
const MAX_LINE_QUANTITY = 10;
const MAX_LINES = 10;
const MAX_TOTAL_QUANTITY = 20;
export const checkoutLineItemRequestSchema = z.object({
    lookupKey: z.string().min(1),
    quantity: z.number().int().min(1).max(MAX_LINE_QUANTITY),
});
// Deliberately no amount field anywhere in this schema -- the server never
// trusts a client-supplied price, so there is nothing to tamper with. Every
// amount is resolved server-side from the catalog mirror.
export const checkoutSessionRequestSchema = z
    .object({
    items: z.array(checkoutLineItemRequestSchema).min(1).max(MAX_LINES),
    email: z.string().email().optional(),
    // Which Checkout return URLs to use -- defaults to the storefront so
    // apps/web's existing call sites need no change. The server owns both
    // URLs; the client never supplies one, so this adds no open-redirect
    // surface to an unauthenticated route.
    source: z.enum(["storefront", "client_app"]).default("storefront"),
    // The gift message itself isn't collected here -- Stripe Checkout asks
    // for it as a custom field on the hosted page, only when this is true
    // (see checkout/routes.ts's createStripeSession).
    isGift: z.boolean().default(false),
    // Ad click identifiers, stored so a paid order can be attributed back to
    // the ad that brought the buyer. Internal attribution only: none of them
    // is ever transmitted to Google or Meta. That is a regulatory position
    // for a healthcare operator, not a configuration choice -- see
    // platform/docs/marketing-tags.md.
    //
    // This route is unauthenticated, so each value is opaque, length-capped
    // and never interpolated anywhere. Their presence *is* the consent
    // signal: apps/web only sends them when hasMarketingConsent() is true
    // (lib/ads-click.ts). None is derived from anything the buyer typed.
    //
    // `fbp` has no source today -- it was a per-browser id Meta's Pixel
    // minted, and no pixel runs on this site. Kept so the contract still
    // matches the `orders` columns.
    gclid: z.string().max(200).optional(),
    fbc: z.string().max(200).optional(),
    fbp: z.string().max(200).optional(),
    // Opaque analytics join key, never trusted for anything. Optional: a
    // buyer who opted out of measurement has no session id at all.
    analyticsSessionId: z.string().max(64).optional(),
})
    .superRefine((val, ctx) => {
    const totalQuantity = val.items.reduce((sum, item) => sum + item.quantity, 0);
    if (totalQuantity > MAX_TOTAL_QUANTITY) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["items"],
            message: `Total quantity across items must not exceed ${MAX_TOTAL_QUANTITY}`,
        });
    }
});
export const checkoutSessionResponseSchema = z.object({
    orderNumber: z.string(),
    checkoutUrl: z.string().url(),
});
// No buyer PII (email/name) -- this is reachable from the success page by
// anyone holding the signed token in the URL, and the page never needs more
// than "did it land yet" to decide what to show.
export const orderStatusResponseSchema = z.object({
    orderNumber: z.string(),
    status: z.enum(["pending", "paid", "payment_failed", "expired", "cancelled"]),
    totalCents: z.number(),
    currency: z.string(),
});
export const fulfillmentKindSchema = z.enum(["lab_panel", "analysis", "bundle", "membership", "follow_up_visit"]);
/* Which fulfilment kinds mean "this line carries laboratory measurements".
 *
 * `bundle` belongs here as much as `lab_panel` does: the membership sells
 * three lab measurements under one line, and a client who bought it needs a
 * draw booked exactly as much as one who bought the panel on its own. It lives
 * in the contract package because four call sites across three apps ask the
 * same question — the journey's `awaiting_draw` fact
 * (apps/api/src/modules/commerce/checkout/store.ts), the clinic timeline's
 * purchase classifier, the client app's order detail, and the storefront's
 * order-received page — and they were already answering it three different
 * ways. `analysis` and `follow_up_visit` are excluded: a wellbeing-analysis
 * call or a follow-up visit issues nothing to present at a laboratory. A
 * follow-up bought with labs carries them on its separate `lab_panel` add-on
 * line.
 */
export const LAB_CARRYING_FULFILLMENT_KINDS = ["lab_panel", "bundle"];
/** Whether a line's fulfilment kind says it carries laboratory measurements.
    Null (an unmirrored or metadata-less product) reads as "no" — see
    platform/docs/commerce.md on a missing `fulfillment_kind`. */
export function carriesLabMeasurements(kind) {
    return kind != null && LAB_CARRYING_FULFILLMENT_KINDS.includes(kind);
}
/* A signed-in client's own order history.
 *
 * Separate from `orderStatusResponseSchema` above, which is the token-authorised
 * read the Checkout success page polls: that one is deliberately thin because
 * anyone holding the URL can call it. This one is behind a session, so it can
 * carry the lines -- which is the whole point of it, since the order number a
 * client quotes at the laboratory means nothing to them without "and this is
 * the panel it is for".
 *
 * Still no buyer PII: the reader is the buyer, so name and email would only be
 * telling them what they already know.
 *
 * `productName` is the line's own snapshot from `order_items`, not i18n copy.
 * A receipt has to say what was actually sold at the time it was sold -- an
 * order placed in the WooCommerce era names the product Woo named, and
 * re-labelling history through today's catalog would make it a different
 * document. This is the one place a product name reaches a client surface from
 * data rather than from reviewed copy, and it is safe there for the same
 * reason: it is a record of a transaction, not a claim about a test.
 */
export const clientOrderItemSchema = z.object({
    productName: z.string(),
    quantity: z.number().int(),
    lineTotalCents: z.number().int(),
    fulfillmentKind: fulfillmentKindSchema.nullable(),
});
export const clientOrderSchema = z.object({
    orderNumber: z.string(),
    /* Which shop the order was placed in. The client app shows a mark on
       anything that isn't `storefront`, because those order numbers come from a
       different series (Woo's own 4-digit one, Glide's `GL-`) and a client
       holding two of them needs to know why they don't look alike. */
    source: z.enum(["storefront", "woocommerce", "glide_legacy", "event_pos"]),
    placedAt: z.string(),
    paidAt: z.string().nullable(),
    currency: z.string(),
    totalCents: z.number().int(),
    /* Zero for an untouched order. The list never states an order's *fulfilment*
       -- whether a paid lab panel has actually been drawn is not knowable for a
       webstore order until the lab integration lands (see the timeline
       contract's note on why no specimen row exists for one), so the only status
       this contract carries is the payment side, which we do know. */
    refundedTotalCents: z.number().int(),
    items: z.array(clientOrderItemSchema),
});
// No `status` field: the route returns paid orders only, so every order here
// has the same one. An abandoned checkout is a Postgres row, not a purchase,
// and listing it back to the client would invite them to hunt for an order
// number that was never issued anything.
//
// customerNumber is account-level, not per-order (same id a client quotes at
// the lab for every order they've placed), so it sits beside `orders` rather
// than duplicated onto each one -- the same wooCustomerId lookup
// BookDrawBlock's home-screen pass already surfaces.
export const clientOrdersResponseSchema = z.object({
    orders: z.array(clientOrderSchema),
    customerNumber: z.string().nullable(),
});
// Deliberately no product name/description here -- copy lives in i18n (and,
// for web, its content collection), keeping MDR-relevant copy inside
// reviewed source rather than this payload. `slug` is the join key to copy;
// `lookupKey` is the join key to checkout.
export const catalogPriceSchema = z.object({
    lookupKey: z.string(),
    unitAmountCents: z.number().int(),
    currency: z.string(),
});
export const catalogProductSchema = z.object({
    slug: z.string(),
    fulfillmentKind: fulfillmentKindSchema.nullable(),
    sortOrder: z.number().int().nullable(),
    prices: z.array(catalogPriceSchema),
});
export const catalogResponseSchema = z.object({ products: z.array(catalogProductSchema) });
